import { Link } from "react-router-dom";
import { Button } from "../components/ui/button";
import Logo from "../components/Logo";
import {
  Bike, Wallet, ShieldCheck, Clock, IndianRupee, Zap, Star, ArrowRight,
  Smartphone, FileCheck, GraduationCap, Rocket, MapPin, Timer,
} from "lucide-react";

const EARNING_EXAMPLES = [
  { label: "Base pickup fee", value: "₹20-25 / job" },
  { label: "Last-mile delivery fee", value: "₹18-22 / delivery" },
  { label: "Distance bonus (>3 km)", value: "+₹5" },
  { label: "Peak-hour surge (8-10 AM, 5-7 PM)", value: "+₹8-15" },
  { label: "College mega-pickup day bonus", value: "+₹12 / job" },
  { label: "Rating bonus (>4.8 & 200+ jobs/month)", value: "+₹500 / month" },
  { label: "Refer an agent (after 50 pickups)", value: "+₹300" },
  { label: "Joining bonus (first 15 agents, 20 jobs)", value: "+₹2,000" },
];

const STEPS = [
  { icon: Smartphone, title: "Apply", desc: "Register with your phone + city in under 2 minutes." },
  { icon: FileCheck, title: "Upload KYC", desc: "Aadhaar, DL, RC, profile photo. Reviewed within 24-48 hrs." },
  { icon: GraduationCap, title: "Complete Training", desc: "3 quick lessons on jobs, scanning and disputes. 30 minutes." },
  { icon: Wallet, title: "₹500 Deposit", desc: "Refundable deposit via Razorpay. Auto-refunded within 24 hours." },
  { icon: Rocket, title: "Go Live", desc: "Start accepting jobs in your city and earn from day one." },
];

export default function BecomeAgent() {
  return (
    <div className="min-h-screen bg-slate-50">
      {/* Nav */}
      <nav className="bg-white/90 backdrop-blur border-b border-slate-100 sticky top-0 z-30">
        <div className="max-w-6xl mx-auto px-4 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2"><Logo size="sm" /></Link>
          <div className="flex items-center gap-2">
            <Link to="/login" className="text-sm font-semibold text-slate-600 hover:text-[#FF6B00] hidden sm:inline" data-testid="agent-existing-login">Existing Agent?</Link>
            <Link to="/become-agent/apply"><Button className="rounded-full bg-[#FF6B00] hover:bg-[#E65A00] text-white px-5" data-testid="hero-apply-btn">Apply Now <ArrowRight className="ml-1.5 h-4 w-4" /></Button></Link>
          </div>
        </div>
      </nav>

      {/* Hero */}
      <section className="relative overflow-hidden bg-gradient-to-br from-[#0F172A] via-[#1E293B] to-[#0F172A] text-white">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_30%_20%,#FF6B00_0%,transparent_40%)] opacity-20"></div>
        <div className="relative max-w-6xl mx-auto px-4 py-20 sm:py-24">
          <span className="inline-flex items-center gap-1.5 bg-[#FF6B00]/15 border border-[#FF6B00]/30 text-[#FF6B00] text-xs font-bold px-3 py-1 rounded-full"><Zap className="h-3 w-3" /> NOW HIRING — Gig Partners</span>
          <h1 className="font-['Outfit'] text-4xl sm:text-5xl lg:text-6xl font-bold mt-5 tracking-tight leading-[1.05]">
            Drive. Earn. <span className="text-[#FF6B00]">Repeat.</span>
          </h1>
          <p className="text-base sm:text-lg text-slate-300 mt-5 max-w-2xl leading-relaxed">
            Be your own boss. Earn <span className="text-white font-bold">₹18,000 – ₹22,500 / month</span> working just 6-7 hours a day.
            No fixed shifts, no targets — just real pickups in your city.
          </p>
          <div className="flex flex-wrap gap-3 mt-8">
            <Link to="/become-agent/apply"><Button size="lg" className="rounded-full bg-[#FF6B00] hover:bg-[#E65A00] text-white px-7 h-12 text-sm font-semibold" data-testid="hero-apply-btn-2">Start Earning Today <ArrowRight className="ml-2 h-4 w-4" /></Button></Link>
            <a href="#how-it-works"><Button size="lg" variant="outline" className="rounded-full border-white/20 bg-white/5 text-white hover:bg-white/10 px-7 h-12 text-sm font-semibold" data-testid="how-it-works-btn">How it Works</Button></a>
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-4 gap-6 mt-14">
            {[
              { icon: IndianRupee, label: "₹600 - ₹750", sub: "daily potential" },
              { icon: Clock, label: "6-7 hrs", sub: "flexible" },
              { icon: MapPin, label: "Your City", sub: "geofenced jobs" },
              { icon: Timer, label: "24 hrs", sub: "deposit refund" },
            ].map((s) => (
              <div key={s.label} className="flex items-center gap-3">
                <div className="h-10 w-10 rounded-xl bg-white/10 border border-white/10 flex items-center justify-center"><s.icon className="h-5 w-5 text-[#FF6B00]" /></div>
                <div><p className="text-lg font-bold">{s.label}</p><p className="text-xs text-slate-400">{s.sub}</p></div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Earnings card */}
      <section className="max-w-5xl mx-auto px-4 -mt-10 relative z-10">
        <div className="bg-white rounded-3xl shadow-xl border border-slate-100 p-6 sm:p-10">
          <div className="flex items-center gap-2 mb-4">
            <IndianRupee className="h-5 w-5 text-[#FF6B00]" />
            <h2 className="font-['Outfit'] text-2xl font-bold text-slate-900">What You Earn</h2>
          </div>
          <p className="text-sm text-slate-500 mb-6">Transparent per-pickup pricing. Bonuses auto-credit to your wallet.</p>
          <div className="grid md:grid-cols-2 gap-3">
            {EARNING_EXAMPLES.map((e, i) => (
              <div key={e.label} className="flex items-center justify-between p-4 rounded-xl border border-slate-100 bg-slate-50/50" data-testid={`earning-row-${i}`}>
                <span className="text-sm text-slate-700">{e.label}</span>
                <span className="font-mono font-bold text-[#FF6B00] text-sm">{e.value}</span>
              </div>
            ))}
          </div>
          <div className="mt-6 p-4 rounded-xl bg-emerald-50 border border-emerald-100 flex gap-3">
            <Star className="h-5 w-5 text-emerald-600 flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-sm font-bold text-emerald-900">30 pickups × ₹22 base = ₹660/day</p>
              <p className="text-xs text-emerald-700 mt-0.5">Add surge + distance bonuses and you can reach ₹750-900/day working peak hours.</p>
            </div>
          </div>
        </div>
      </section>

      {/* How it works */}
      <section id="how-it-works" className="max-w-5xl mx-auto px-4 py-20">
        <h2 className="font-['Outfit'] text-3xl sm:text-4xl font-bold text-slate-900 mb-2">How to Become an Agent</h2>
        <p className="text-slate-500 mb-10">5 steps. Most partners go live within 48 hours.</p>
        <div className="grid sm:grid-cols-2 lg:grid-cols-5 gap-4">
          {STEPS.map((s, i) => (
            <div key={s.title} className="relative p-5 rounded-2xl border border-slate-100 bg-white shadow-sm" data-testid={`step-${i + 1}`}>
              <div className="absolute -top-3 -left-3 h-8 w-8 rounded-xl bg-[#FF6B00] text-white text-sm font-bold flex items-center justify-center shadow">{i + 1}</div>
              <s.icon className="h-8 w-8 text-[#FF6B00] mb-3" />
              <h3 className="font-['Outfit'] font-bold text-slate-900 mb-1">{s.title}</h3>
              <p className="text-xs text-slate-500 leading-relaxed">{s.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Requirements + deposit note */}
      <section className="bg-white border-y border-slate-100 py-16">
        <div className="max-w-5xl mx-auto px-4 grid md:grid-cols-2 gap-8">
          <div>
            <h2 className="font-['Outfit'] text-2xl font-bold text-slate-900 mb-4">What You Need</h2>
            <ul className="space-y-3">
              {["A smartphone with internet", "Your own 2-wheeler (or van/truck)", "Aadhaar + Driving Licence + Vehicle RC", "Indian bank account for payouts", "Age 18+ and a valid address"].map((r) => (
                <li key={r} className="flex items-center gap-2 text-sm text-slate-700"><ShieldCheck className="h-4 w-4 text-emerald-600" /> {r}</li>
              ))}
            </ul>
          </div>
          <div className="rounded-2xl p-6 bg-gradient-to-br from-orange-50 to-white border border-orange-100">
            <div className="flex items-center gap-2 mb-2">
              <Wallet className="h-5 w-5 text-[#FF6B00]" />
              <h3 className="font-['Outfit'] font-bold text-slate-900">₹500 Refundable Deposit</h3>
            </div>
            <p className="text-sm text-slate-600 leading-relaxed">A one-time ₹500 security deposit is collected via Razorpay during onboarding. This protects both riders and customers from no-shows and fraud.</p>
            <div className="mt-4 p-3 rounded-xl bg-white border border-emerald-100">
              <p className="text-xs font-bold text-emerald-700 flex items-center gap-1.5"><Clock className="h-3.5 w-3.5" /> Auto-refunded to your source account within 24 hours</p>
            </div>
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="max-w-4xl mx-auto px-4 py-20 text-center">
        <Bike className="h-12 w-12 text-[#FF6B00] mx-auto mb-4" />
        <h2 className="font-['Outfit'] text-3xl sm:text-4xl font-bold text-slate-900 mb-3">Ready to start?</h2>
        <p className="text-slate-500 mb-7 text-base">Join India's fastest-growing gig-logistics platform.</p>
        <Link to="/become-agent/apply"><Button size="lg" className="rounded-full bg-[#FF6B00] hover:bg-[#E65A00] text-white px-10 h-12 text-sm font-semibold" data-testid="cta-apply-btn">Apply Now <ArrowRight className="ml-2 h-4 w-4" /></Button></Link>
      </section>
    </div>
  );
}
